import React, { useEffect, useState } from "react";
import { Control, FieldErrors } from "react-hook-form";
import { ensure, FormDropdown, getById, mapToDropdownOption } from "sprint-common-web";

import { EncodeSpecification } from "../../../../common/types/EncodeSpecification";
import {
    EncodeSpecificationContentType,
    mapContentToEncodeSpecificationContentType
} from "../../../../common/types/EncodeSpecificationContentType";
import { handleOptionChange } from "../../../../common/utils/handleOptionChange";
import { SystemPlatform } from "../../../common/types/OrdersAirline";
import { SystemPlatformSpecification } from "../../../common/types/SystemPlatformSpecification";
import { useEncodeSpecifications } from "../common/hooks/useEncodeSpecifications";
import { useCreateOrder } from "../common/provider/useCreateOrder";

interface OrderEncodeSpecificationSelectorProps {
    label: string;
    name: string;
    control: Control;
    errors: FieldErrors;
    dependsOn: string[];
}

export const OrderEncodeSpecificationSelector: React.FC<OrderEncodeSpecificationSelectorProps> = ({
    label,
    name,
    control,
    errors,
    dependsOn
}: OrderEncodeSpecificationSelectorProps) => {
    const { data: encodeSpecifications } = useEncodeSpecifications();
    const { system, airline, contentType, setEncodeSpecification, contentVersion } = useCreateOrder();

    const [availableEncodeSpecifications, setAvailableEncodeSpecifications] = useState<EncodeSpecification[]>([]);

    useEffect(() => {
        if (system && airline && contentType && contentVersion) {
            const systemPlatform = getSystemPlatform(airline.systemPlatforms, system.id);
            const encodeSpecs = getEncodeSpecifications(
                systemPlatform.specifications,
                mapContentToEncodeSpecificationContentType({
                    contentSubtype: contentType,
                    contentVersionType: contentVersion?.version.type
                }),
                encodeSpecifications ?? []
            );

            setAvailableEncodeSpecifications(
                encodeSpecs.defaultEncodeSpecification
                    ? [encodeSpecs.defaultEncodeSpecification, ...encodeSpecs.encodeSpecifications]
                    : []
            );
        } else {
            setAvailableEncodeSpecifications([]);
        }
    }, [system, airline, contentType, contentVersion]);

    const handleEncodeSpecificationSelected = (value: string) => {
        const selectedEncodeSpecification = getById(encodeSpecifications ?? [], value);

        setEncodeSpecification(selectedEncodeSpecification);
    };

    const handleEncodeSpecificationUnselected = () => {
        setEncodeSpecification(undefined);
    };

    return (
        <FormDropdown
            label={label}
            placeholder="Search and select encode specification"
            name={name}
            options={availableEncodeSpecifications.map((item) => mapToDropdownOption(item))}
            disabled={!availableEncodeSpecifications.length}
            dependsOn={dependsOn}
            control={control}
            errors={errors}
            rules={{ required: "Select encode specification" }}
            onChange={handleOptionChange(handleEncodeSpecificationSelected, handleEncodeSpecificationUnselected)}
        />
    );
};

const getSystemPlatform = (systemPlatforms: SystemPlatform[], systemId: string) => {
    const systemPlatform = systemPlatforms.find((sysPlatform: SystemPlatform) => sysPlatform.system.id === systemId);

    ensure(systemPlatform);

    return systemPlatform;
};

const getEncodeSpecifications = (
    specifications: SystemPlatformSpecification[],
    encodeSpecificationContentType: EncodeSpecificationContentType,
    allEncodeSpecs: EncodeSpecification[]
) => {
    const specs = specifications.find(
        (spec: SystemPlatformSpecification) => spec.encodeSpecificationContentType === encodeSpecificationContentType
    );

    if (!specs?.defaultEncodeSpecification) {
        return {
            defaultEncodeSpecification: undefined,
            encodeSpecifications: []
        };
    }

    const defaultEncodeSpecification = allEncodeSpecs.find((spec) => spec.id === specs?.defaultEncodeSpecification?.id);

    const encodeSpecifications = allEncodeSpecs.filter((item) =>
        specs?.encodeSpecifications.some((encodeSpec) => encodeSpec.id === item.id)
    );

    return {
        defaultEncodeSpecification,
        encodeSpecifications
    };
};
